import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useV2Auth } from '@/context/V2AuthContext';
import { useTranslation } from 'react-i18next';
import { Heart, Users, Waves, Send } from 'lucide-react';

function ChainNode({ node, level }) {
  return (
    <div className={level > 0 ? 'ml-5 sm:ml-7 border-l-2 border-[var(--primary)]/25 pl-4 sm:pl-5' : ''}>
      <div className="flex items-center gap-2.5 py-2" data-testid="chain-node">
        <div className="w-8 h-8 rounded-full bg-[var(--primary)]/10 flex items-center justify-center shrink-0">
          <span className="text-sm font-semibold text-[var(--primary)]">{(node.name || '?')[0].toUpperCase()}</span>
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-[var(--text)] truncate">{node.name}</div>
          {node.sentAt && (
            <div className="text-[10px] text-[var(--muted)]">
              {new Date(node.sentAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </div>
          )}
        </div>
        {node.children?.length > 0 && (
          <span className="ml-auto text-[10px] rounded-full bg-[var(--primary)]/10 text-[var(--primary)] px-2 py-0.5 whitespace-nowrap">
            <Heart className="w-2.5 h-2.5 inline mr-0.5 -mt-0.5" />
            {node.children.length}
          </span>
        )}
      </div>
      {node.children?.map((child) => (
        <ChainNode key={child.id + child.sentAt} node={child} level={level + 1} />
      ))}
    </div>
  );
}

export default function CarmaChainV2() {
  const { API_V2 } = useV2Auth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await axios.get(`${API_V2}/carma/chain`, { withCredentials: true });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled && err.response?.status === 401) navigate('/auth', { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [API_V2, navigate]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-sm text-[var(--muted)]">{t('common.loading')}</div>
      </div>
    );
  }
  if (!data) return null;

  const { stats, tree } = data;
  const isEmpty = tree.length === 0;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6" data-testid="chain-page">
      <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-6 shadow-sm space-y-2">
        <h1 className="text-2xl font-semibold text-[var(--text)]">{t('chain.title')}</h1>
        <p className="text-sm text-[var(--muted)]">{t('chain.subtitle')}</p>
      </div>

      {isEmpty ? (
        <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-8 shadow-sm text-center space-y-4" data-testid="chain-empty">
          <Waves className="w-8 h-8 mx-auto text-[var(--primary)]/50" />
          <p className="text-sm text-[var(--muted)]">{t('chain.empty')}</p>
          <button
            type="button"
            onClick={() => navigate('/give')}
            data-testid="chain-empty-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] px-5 py-2 text-sm hover:opacity-90"
          >
            <Send className="w-4 h-4" />
            {t('chain.emptyCta')}
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-4 shadow-sm text-center" data-testid="chain-stat-direct">
              <Send className="w-4 h-4 mx-auto text-[var(--primary)] mb-1" />
              <div className="text-xl font-semibold text-[var(--text)]">{stats.directReach}</div>
              <div className="text-[10px] text-[var(--muted)]">{t('chain.directReach')}</div>
            </div>
            <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-4 shadow-sm text-center" data-testid="chain-stat-echoes">
              <Waves className="w-4 h-4 mx-auto text-[var(--primary)] mb-1" />
              <div className="text-xl font-semibold text-[var(--text)]">{stats.echoes}</div>
              <div className="text-[10px] text-[var(--muted)]">{t('chain.echoes')}</div>
            </div>
            <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-4 shadow-sm text-center" data-testid="chain-stat-total">
              <Users className="w-4 h-4 mx-auto text-[var(--primary)] mb-1" />
              <div className="text-xl font-semibold text-[var(--text)]">{stats.totalReach}</div>
              <div className="text-[10px] text-[var(--muted)]">{t('chain.totalReach')}</div>
            </div>
          </div>

          <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-6 shadow-sm space-y-3" data-testid="chain-tree">
            <div className="flex items-center gap-2.5 pb-2 border-b border-[var(--border)]">
              <div className="w-9 h-9 rounded-full bg-[var(--primary)] flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <div className="text-sm font-semibold text-[var(--text)]">{t('chain.you')} ({data.you})</div>
            </div>
            {tree.map((node) => (
              <ChainNode key={node.id + node.sentAt} node={node} level={0} />
            ))}
            <p className="text-[11px] text-[var(--muted)] pt-2 border-t border-[var(--border)]">{t('chain.legend')}</p>
          </div>
        </>
      )}
    </div>
  );
}
